import {
  localDb,
  clearLocalDb,
  discardOutbox,
  requestPersistentStorage,
  withSyncLock,
  OUTBOX_META,
  SYNC_LOCK,
  type TombstoneRow,
} from "@/lib/localDb";
import { uuidv7 } from "@/lib/uuid";
import { subscribeToChanges } from "@/lib/liveSync";
import { reloadApp, requestBackgroundSync } from "@/lib/swClient";
import { csrfToken } from "@/lib/csrf";
import { redirectToSignIn } from "@/lib/fetchSideEffect";
import { planEnqueue } from "@/lib/mutators/outbox";
import { SYNC_PROTOCOL, type MutationArgs, type MutationName, type OutboxOp, type PersistedOp } from "@/lib/mutators/types";
import {
  announce,
  claimOutbox,
  dropRejected,
  fetchInTime,
  pushBatch,
  readAckedThrough,
  readOutbox,
  readOutboxUser,
  readSentThrough,
  rejectionsIn,
  rememberCsrfToken,
  renewClientId,
  syncChannel,
  CLIENT_ID_TAKEN,
  CSRF_STALE,
  SIGNED_OUT,
  type ChannelMessage,
  type OutboxBatch,
  type Rejection,
  type RejectReason,
} from "@/lib/outboxPush";
import {
  serverMicros,
  TOMBSTONE_TTL_MS,
  tombstoneStamps,
  unsentAfter,
  unsentCount,
  useTaskStore,
  type PersistedTask,
  type SyncList,
  type SyncMeta,
  type SyncTask,
} from "@/stores/taskStore";
import { useForeignOutbox } from "@/stores/foreignOutbox";

export interface SyncPullPage {
  userId: string;
  cursor: number;
  hasMore: boolean;
  reset: boolean;
  // The clock value the snapshot started from; a continuation sends it back. Null for increments.
  snapshot: number | null;
  serverTime: string;
  today: string;
  timeOfDay: string;
  zone: string;
  tasks: SyncTask[];
  lists: SyncList[];
}

// An op that never reached the outbox: IndexedDB could not be opened, or it holds another
// account's unsent changes the user has not answered about yet.
export type UnsavedReason = "storage_unavailable" | "outbox_held";

export interface OpRejection extends Omit<Rejection, "reason"> {
  reason: RejectReason | UnsavedReason;
  // Whether this tab made the op or one merged into it; every tab hears about every rejection.
  enqueuedHere: boolean;
}

export const LIVE_CHANGE_DEBOUNCE_MS = 300;
export const PUSH_DEBOUNCE_MS = 300;
export const RETRY_DELAYS_MS = { first: 1_000, max: 60_000 };
// An IndexedDB open can hang (a blocked upgrade, a stuck browser); ops must not wait on it forever.
export const STORAGE_OPEN_TIMEOUT_MS = 5_000;
export { SYNC_LOCK, TOMBSTONE_TTL_MS };
export { MAX_MUTATIONS_PER_PUSH, REQUEST_TIMEOUT_MS as PULL_TIMEOUT_MS, type RejectReason, type SyncPushResponse } from "@/lib/outboxPush";

const META_KEYS = ["cursor", "snapshotBase", "today", "timeOfDay", "zone", "userId"] as const;
const COMPLETE_KEY = "complete";
// Changes whenever a tab rebuilds the copy from scratch, so the others notice their
// incremental base is gone.
const RUN_KEY = "run";
// Each tab's channel objects hear its own messages too.
const TAB_ID = uuidv7();
// sentThrough: the highest seq any tab has put into a push request. Those ops may be on
// the server already, so nothing merges into them or cancels them any more.
const { clientId: CLIENT_ID_KEY, sentThrough: SENT_THROUGH_KEY, userId: OUTBOX_USER_KEY, ackedThrough: ACKED_THROUGH_KEY } = OUTBOX_META;

const OUTBOX_CHANGED: ChannelMessage = { type: "outbox-changed", from: TAB_ID };

let userId: string | null = null;
// Bumped whenever the engine stops or changes user: a response for an older
// generation belongs to someone else's session and is dropped.
let generation = 0;
let inFlight: Promise<void> | null = null;
let pullAgain = false;
let stopTriggers: (() => void) | null = null;
let engineAbort: AbortController | null = null;
// The run of the IndexedDB copy this tab's memory continues.
let runId: string | null = null;
// Set when IndexedDB lags behind memory (a failed write, a wipe by another tab): the next
// page is persisted as the whole store, not as an increment.
let needsFullWrite = false;

// Outbox state of this tab. Ops are written one after another, in the order they were made.
let outboxWrites: Promise<void> = Promise.resolve();
let outboxAvailable = false;
// IndexedDB is read when the engine starts and again after a failed attempt; ops made
// meanwhile wait in memory for the answer.
let storage: "opening" | "open" | "failed" = "opening";
let storageOpening: Promise<void> = Promise.resolve();
// Set by a 401 (session gone) or a 426 (this bundle speaks an old protocol); the outbox waits.
let pushPaused = false;
let pushTimer: ReturnType<typeof setTimeout> | undefined;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let retryDelay = RETRY_DELAYS_MS.first;
const enqueuedHere = new Set<string>();
// Unwritten ops with the user they were made for.
const unwritten = new Map<string, { op: OutboxOp; owner: string }>();
const rejectionListeners = new Set<(rejection: OpRejection) => void>();
const outdatedListeners = new Set<() => void>();

interface Persisted {
  tasks: PersistedTask[];
  lists: SyncList[];
  meta: Map<string, unknown>;
  tombstones: Record<string, string>;
  outbox: PersistedOp[];
}

async function fetchPage(cursor: number, snapshot: number | null, engineSignal: AbortSignal): Promise<SyncPullPage> {
  const params = new URLSearchParams({ cursor: String(cursor) });
  if (snapshot !== null) params.set("snapshot", String(snapshot));
  return fetchInTime(`/sync/pull?${params}`, { headers: { Accept: "application/json" } }, engineSignal, async (response) => {
    if (!response.ok) throw new Error(`sync pull failed: ${response.status}`);
    return (await response.json()) as SyncPullPage;
  });
}

async function readMeta(): Promise<Map<string, unknown> | null> {
  try {
    return new Map((await localDb.meta.toArray()).map((row) => [row.key, row.value]));
  } catch {
    return null;
  }
}

async function readPersisted(): Promise<Persisted | null> {
  try {
    return await localDb.transaction("r", [localDb.tasks, localDb.lists, localDb.meta, localDb.tombstones, localDb.outbox], async () => ({
      tasks: await localDb.tasks.toArray(),
      lists: await localDb.lists.toArray(),
      meta: new Map((await localDb.meta.toArray()).map((row) => [row.key, row.value])),
      tombstones: Object.fromEntries((await localDb.tombstones.toArray()).map((row) => [row.id, row.pulledAt])),
      outbox: await localDb.outbox.orderBy("seq").toArray(),
    }));
  } catch {
    return null;
  }
}

function persistedMeta(meta: Map<string, unknown>): SyncMeta {
  const text = (key: string) => (meta.get(key) as string | null | undefined) ?? null;
  const base = meta.get("snapshotBase");
  return {
    cursor: Number(meta.get("cursor") ?? 0),
    snapshotBase: typeof base === "number" ? base : null,
    today: text("today"),
    timeOfDay: text("timeOfDay"),
    zone: text("zone"),
    userId: text("userId"),
  };
}

function adopt(persisted: Persisted): void {
  const store = useTaskStore.getState();
  store.loadPersisted(persisted.tasks, persisted.lists, persistedMeta(persisted.meta), persisted.tombstones);
  store.setReady(persisted.meta.get(COMPLETE_KEY) === true);
  showOutbox(persisted.outbox, ackedThroughIn(persisted.meta));
  runId = (persisted.meta.get(RUN_KEY) as string | undefined) ?? null;
  needsFullWrite = false;
}

function tombstoneRows(stamps: Record<string, string>): TombstoneRow[] {
  return Object.entries(stamps).map(([id, pulledAt]) => ({ id, pulledAt, at: serverMicros(pulledAt) / 1000 }));
}

// The whole store as IndexedDB rows.
function storeRows(): { tasks: PersistedTask[]; lists: SyncList[]; tombstones: TombstoneRow[] } {
  const { tasks, lists, pulledAt } = useTaskStore.getState();
  return {
    tasks: Object.values(tasks).map((task) => ({ ...task, pulledAt: pulledAt[task.id] })),
    lists: Object.values(lists),
    tombstones: tombstoneRows(tombstoneStamps({ tasks, pulledAt })),
  };
}

async function persistPage(page: SyncPullPage, meta: SyncMeta, complete: boolean): Promise<void> {
  const { tasks } = page;
  const kept = (row: { discardedAt: string | null }) => !row.discardedAt;
  const ids = (rows: { id: string }[]) => rows.map((row) => row.id);
  const rebuild = page.reset || needsFullWrite;
  const nextRun = rebuild ? uuidv7() : runId;
  const full = needsFullWrite && !page.reset ? storeRows() : null;
  const pageTombstones = tombstoneRows(Object.fromEntries(tasks.filter((t) => !kept(t)).map((t) => [t.id, page.serverTime])));
  try {
    await localDb.transaction("rw", [localDb.tasks, localDb.lists, localDb.meta, localDb.tombstones], async () => {
      if (rebuild) await Promise.all([localDb.tasks.clear(), localDb.lists.clear()]);
      if (full) {
        await localDb.tasks.bulkPut(full.tasks);
        await localDb.lists.bulkPut(full.lists);
        await localDb.tombstones.bulkPut(full.tombstones);
      } else {
        await localDb.tasks.bulkPut(tasks.filter(kept).map((task) => ({ ...task, pulledAt: page.serverTime })));
        await localDb.lists.bulkPut(page.lists.filter(kept));
        await localDb.tasks.bulkDelete(ids(tasks.filter((t) => !kept(t))));
        await localDb.lists.bulkDelete(ids(page.lists.filter((l) => !kept(l))));
        await localDb.tombstones.bulkPut(pageTombstones);
      }
      await localDb.tombstones.where("at").below(serverMicros(page.serverTime) / 1000 - TOMBSTONE_TTL_MS).delete();
      await localDb.meta.bulkPut([
        ...META_KEYS.map((key) => ({ key, value: meta[key] })),
        { key: COMPLETE_KEY, value: complete },
        { key: RUN_KEY, value: nextRun },
      ]);
    });
    runId = nextRun;
    needsFullWrite = false;
  } catch {
    // The in-memory store is up to date; IndexedDB catches up with the next page.
    needsFullWrite = true;
  }
}

// The copy this tab continues was wiped or rebuilt by another tab, or another tab
// pulled further while this one waited for the lock.
async function catchUpWithOtherTabs(myGeneration: number): Promise<void> {
  const meta = await readMeta();
  if (!meta || myGeneration !== generation) return;
  const stored = persistedMeta(meta);
  const store = useTaskStore.getState();

  if (stored.userId !== userId) {
    // An increment on top of nothing would be taken for a complete copy.
    if (store.ready && store.meta.snapshotBase === null) needsFullWrite = true;
    else store.upsert([], [], { cursor: 0, snapshotBase: null });
    return;
  }
  const sameRun = meta.get(RUN_KEY) === runId;
  if (sameRun && needsFullWrite) return;
  if (sameRun && stored.cursor === store.meta.cursor && stored.snapshotBase === store.meta.snapshotBase) return;

  const persisted = await readPersisted();
  if (persisted && myGeneration === generation) adopt(persisted);
}

function pageMeta(page: SyncPullPage): Partial<SyncMeta> {
  return {
    cursor: page.cursor,
    snapshotBase: page.hasMore ? page.snapshot : null,
    today: page.today,
    timeOfDay: page.timeOfDay,
    zone: page.zone,
    userId,
  };
}

// The pages of a reset, applied at once: the copy they replace stays on screen until then.
async function swapInReset(pages: SyncPullPage[]): Promise<void> {
  const store = useTaskStore.getState();
  const [first, ...rest] = pages;
  const last = pages[pages.length - 1];
  store.replaceAll(first.tasks, first.lists, {}, first.serverTime);
  for (const page of rest) store.upsert(page.tasks, page.lists, {}, page.serverTime);
  store.upsert([], [], pageMeta(last));
  needsFullWrite = true;
  await persistPage({ ...last, reset: false }, useTaskStore.getState().meta, true);
}

async function pullPages(myGeneration: number, signal: AbortSignal): Promise<void> {
  // A reset over a whole copy is collected before it replaces anything; a lost round
  // drops it, and the next one asks for the reset again.
  let resetPages: SyncPullPage[] | null = null;
  for (;;) {
    if (myGeneration !== generation) return;
    const collected = resetPages?.[resetPages.length - 1];
    const { cursor, snapshotBase } = collected ? { cursor: collected.cursor, snapshotBase: collected.snapshot } : useTaskStore.getState().meta;
    const page = await fetchPage(cursor, snapshotBase, signal);
    if (myGeneration !== generation) return;
    if (page.userId !== userId) {
      stopSyncEngine();
      return;
    }

    const done = !page.hasMore;
    const store = useTaskStore.getState();
    if (page.reset && store.ready) resetPages = [];
    if (resetPages) {
      resetPages.push(page);
      if (!done) continue;
      await swapInReset(resetPages);
      return;
    }

    const complete = done || (page.snapshot === null && store.ready);
    const meta = pageMeta(page);
    if (page.reset) store.replaceAll(page.tasks, page.lists, meta, page.serverTime);
    else store.upsert(page.tasks, page.lists, meta, page.serverTime);
    store.setReady(complete);
    await persistPage(page, useTaskStore.getState().meta, complete);
    if (done) return;
  }
}

// ── Outbox ───────────────────────────────────────────────────────────────────

// Ops this tab made but has not written yet stay after the stored ones. While the outbox
// holds another account's ops, none of them is shown.
function showOutbox(stored: PersistedOp[], ackedThrough: number, written?: string): void {
  if (!outboxAvailable) stored = [];
  const store = useTaskStore.getState();
  const storedIds = new Set(stored.map((op) => op.id));
  const unwritten = store.pending.filter((op) => op.seq === null && op.id !== written && !storedIds.has(op.id));
  store.setPending([...stored, ...unwritten], ackedThrough);
}

const ackedThroughIn = (meta: Map<string, unknown>) => Number(meta.get(ACKED_THROUGH_KEY) ?? 0);

function dropFromPending(keep: (op: OutboxOp) => boolean): void {
  const store = useTaskStore.getState();
  store.setPending(store.pending.filter(keep));
}

// An op that could not be written stays on screen unwritten; the next round writes it. A
// write that lands after the engine stopped still keeps the op, just off this tab's screen.
// An op made before IndexedDB answered is written only if the outbox turned out to be open.
// Another account's unsent ops are never cleared to make room: the user is asked first.
class OutboxHeld extends Error {
  constructor(readonly unsent: number) {
    super("outbox held by another account");
  }
}

async function writeOp(op: OutboxOp, owner: string, myGeneration: number, madeBeforeOpen = false): Promise<void> {
  const current = myGeneration === generation;
  if (madeBeforeOpen && current && !outboxAvailable) {
    refuse(op, storage === "open" ? "outbox_held" : "storage_unavailable");
    return;
  }
  let stored: { ops: PersistedOp[]; ackedThrough: number };
  try {
    stored = await localDb.transaction("rw", [localDb.outbox, localDb.meta], async () => {
      const outboxUser = await readOutboxUser();
      if (outboxUser !== owner) {
        const unsent = await localDb.outbox.where("seq").above(await readAckedThrough()).count();
        if (outboxUser !== undefined && unsent > 0) throw new OutboxHeld(unsent);
        await localDb.outbox.clear();
        await localDb.meta.bulkDelete([CLIENT_ID_KEY, SENT_THROUGH_KEY, ACKED_THROUGH_KEY]);
      }
      const plan = planEnqueue(await readOutbox(), op, await readSentThrough());
      await localDb.outbox.bulkDelete(plan.remove);
      if (plan.add) {
        const { seq: _unnumbered, ...row } = plan.add;
        await localDb.outbox.add({ ...row, protocol: SYNC_PROTOCOL } as PersistedOp);
      }
      await localDb.meta.put({ key: OUTBOX_USER_KEY, value: owner });
      return { ops: await readOutbox(), ackedThrough: await readAckedThrough() };
    });
  } catch (error) {
    // After a stop the op belongs to a session that is gone: nobody is told.
    if (!current) return;
    if (error instanceof OutboxHeld) {
      outboxAvailable = false;
      useForeignOutbox.setState({ count: error.unsent });
      refuse(op, "outbox_held");
    } else unwritten.set(op.id, { op, owner });
    return;
  }
  if (myGeneration !== generation) return;
  unwritten.delete(op.id);
  showOutbox(stored.ops, stored.ackedThrough, op.id);
  announce(OUTBOX_CHANGED);
}

function writeUnwritten(): Promise<void> {
  const myGeneration = generation;
  for (const { op, owner } of unwritten.values()) outboxWrites = outboxWrites.then(() => writeOp(op, owner, myGeneration));
  unwritten.clear();
  return outboxWrites;
}

// Once written, so the worker finds the ops when it runs.
function handOutboxToWorker(): void {
  const myGeneration = generation;
  void outboxWrites.then(() => (myGeneration === generation ? requestBackgroundSync(csrfToken()) : undefined));
}

function schedulePush(): void {
  clearTimeout(pushTimer);
  pushTimer = setTimeout(() => void pull(), PUSH_DEBOUNCE_MS);
}

/**
 * Records a change: it shows in every local view at once, is written to the outbox
 * shared by the tabs, and is pushed shortly after. Use while canEnqueue() holds.
 */
export function enqueue<N extends MutationName>(name: N, args: MutationArgs[N]): OutboxOp {
  const op = { id: uuidv7(), seq: null, name, args, createdAt: new Date().toISOString() } as OutboxOp;
  const owner = userId as string;
  const myGeneration = generation;
  const madeBeforeOpen = !outboxAvailable;
  enqueuedHere.add(op.id);
  useTaskStore.getState().addPending(op);
  const write = () => {
    outboxWrites = outboxWrites.then(() => writeOp(op, owner, myGeneration, madeBeforeOpen));
  };
  // Not chained into outboxWrites: opening may wait for the lock a round holds while it writes.
  const writeOnceOpen = (): void => {
    if (storage === "opening" && myGeneration === generation) {
      void storageOpening.then(writeOnceOpen);
      return;
    }
    write();
    schedulePush();
  };
  if (madeBeforeOpen) {
    void reopenStorage();
    writeOnceOpen();
  } else write();
  schedulePush();
  if (!navigator.onLine) handOutboxToWorker();
  return op;
}

// Settles once every op enqueued so far is written to IndexedDB (or failed to be).
export async function outboxWritten(): Promise<void> {
  while (storage === "opening" && userId !== null) await storageOpening;
  await outboxWrites;
}

// The outbox can take changes: the engine runs for a user, with no other account's unsent
// changes waiting. While IndexedDB is still being opened (or opened again after a failure)
// an op waits for it, and is refused if it stays closed. An op made before the copy is
// whole shows once it is.
export function canEnqueue(): boolean {
  return userId !== null && (outboxAvailable || storage !== "open");
}

// Changes are kept on this device right now, not just waiting to find out.
export function outboxOpen(): boolean {
  return userId !== null && outboxAvailable;
}

function refuse(op: OutboxOp, reason: UnsavedReason): void {
  dropFromPending((pendingOp) => pendingOp.id !== op.id);
  for (const listener of rejectionListeners) listener({ op, reason, enqueuedHere: true });
}

/**
 * Sends what waits in the outbox: settles once a round has pushed it (or failed to), at
 * once when nothing waits or the engine is off. A request that goes to the server
 * directly awaits it, so it does not overtake older changes.
 */
export async function flushOutbox(): Promise<void> {
  if (!userId || pushPaused || useTaskStore.getState().pending.length === 0) return;
  await outboxWritten();
  if (!userId || useTaskStore.getState().pending.length === 0) return;
  clearTimeout(pushTimer);
  await pull();
}

// Called in every tab for every rejected op; see OpRejection.enqueuedHere.
export function onOpRejected(listener: (rejection: OpRejection) => void): () => void {
  rejectionListeners.add(listener);
  return () => rejectionListeners.delete(listener);
}

// The server no longer speaks this bundle's protocol: the outbox waits for a reload.
export function onProtocolOutdated(listener: () => void): () => void {
  outdatedListeners.add(listener);
  return () => outdatedListeners.delete(listener);
}

const madeHere = (op: OutboxOp) => [op.id, ...(op.mergedIds ?? [])].some((id) => enqueuedHere.has(id));

function report(rejections: Rejection[]): void {
  const gone = new Set(rejections.map((r) => r.op.id));
  dropFromPending((op) => !gone.has(op.id));
  for (const rejection of rejections) {
    const event = { ...rejection, enqueuedHere: madeHere(rejection.op) };
    for (const listener of rejectionListeners) listener(event);
  }
}

// Any response refreshes the XSRF-TOKEN cookie; a pull is a request the round makes anyway.
async function refreshCsrfCookie(signal: AbortSignal): Promise<void> {
  const { cursor, snapshotBase } = useTaskStore.getState().meta;
  await fetchPage(cursor, snapshotBase, signal);
}

// The seq the server has applied everything up to, or null when nothing was pushed.
async function pushOutbox(myGeneration: number, signal: AbortSignal): Promise<number | null> {
  if (pushPaused) return null;
  await writeUnwritten();
  const claimed = userId ? await claimOutbox(userId) : null;
  if (!claimed || myGeneration !== generation) return null;
  if (claimed.more) pullAgain = true;
  let batch: OutboxBatch = claimed.batch;

  let result = await pushBatch(batch, csrfToken(), signal);
  if (result === CLIENT_ID_TAKEN && myGeneration === generation) {
    batch = { ...batch, clientId: await renewClientId() };
    result = await pushBatch(batch, csrfToken(), signal);
  }
  if (result === CSRF_STALE && myGeneration === generation) {
    await refreshCsrfCookie(signal);
    result = await pushBatch(batch, csrfToken(), signal);
  }
  if (myGeneration !== generation) return null;
  if (result === CLIENT_ID_TAKEN || result === CSRF_STALE) throw new Error(`sync push refused: ${result}`);
  if (result === 401 || result === 426) {
    pushPaused = true;
    if (result === 401) void redirectToSignIn();
    if (result === 426) for (const listener of outdatedListeners) listener();
    return null;
  }

  const rejections = rejectionsIn(batch, result);
  if (rejections.length > 0) {
    await dropRejected(rejections);
    report(rejections);
    announce({ type: "rejected", from: TAB_ID, rejections });
  }
  return result.lastMutationSeq;
}

// After the pull that brought their effects: until then the view needs them replayed.
async function dropAcknowledged(lastMutationSeq: number): Promise<void> {
  await localDb.outbox.where("seq").belowOrEqual(lastMutationSeq).delete().catch(() => {});
  dropFromPending((op) => op.seq === null || op.seq > lastMutationSeq);
  announce(OUTBOX_CHANGED);
}

// Another tab changed the outbox, and maybe the copy under it.
async function refreshOutbox(myGeneration: number): Promise<void> {
  try {
    await withSyncLock(async () => {
      await catchUpWithOtherTabs(myGeneration);
      await settleForeignOutboxElsewhere(myGeneration);
      const stored = await readOutbox();
      const ackedThrough = await readAckedThrough();
      if (myGeneration === generation) showOutbox(stored, ackedThrough);
    });
  } catch {
    // The next round reads the outbox again.
  }
}

class PushFailed extends Error {}

async function syncRound(myGeneration: number, signal: AbortSignal): Promise<void> {
  await catchUpWithOtherTabs(myGeneration);
  let acknowledged: number | null = null;
  let pushFailed = false;
  try {
    acknowledged = await pushOutbox(myGeneration, signal);
  } catch {
    pushFailed = true;
  }
  await pullPages(myGeneration, signal);
  if (acknowledged !== null && myGeneration === generation) await dropAcknowledged(acknowledged);
  if (pushFailed) throw new PushFailed();
  if (myGeneration === generation) await rememberCsrfToken(csrfToken()).catch(() => {});
}

// Waiting ops are retried with a growing delay; a trigger (online, visible, poke) goes first.
function settleRetry(failed: boolean): void {
  clearTimeout(retryTimer);
  retryTimer = undefined;
  if (!failed) {
    retryDelay = RETRY_DELAYS_MS.first;
    return;
  }
  if (pushPaused || useTaskStore.getState().pending.length === 0) return;
  handOutboxToWorker();
  retryTimer = setTimeout(() => void pull(), retryDelay);
  retryDelay = Math.min(retryDelay * 2, RETRY_DELAYS_MS.max);
}

async function runPulls(myGeneration: number): Promise<void> {
  const signal = engineAbort?.signal;
  if (!signal) return;
  let failed = false;
  try {
    await reopenStorage();
    do {
      if (myGeneration !== generation) return;
      pullAgain = false;
      await withSyncLock(() => syncRound(myGeneration, signal));
    } while (pullAgain && myGeneration === generation);
  } catch {
    // Offline, a timeout or a server error: the next trigger resumes from the stored cursor and snapshot base.
    failed = true;
  } finally {
    if (myGeneration === generation) inFlight = null;
  }
  if (myGeneration === generation) settleRetry(failed);
}

// One round: push the outbox, pull, drop what the server confirmed. Single-flight: a call
// while a round is running joins it and makes it go one more round, so a change announced
// mid-round is not missed.
export function pull(): Promise<void> {
  if (!userId) return Promise.resolve();
  if (inFlight) {
    pullAgain = true;
    return inFlight;
  }
  inFlight = runPulls(generation);
  return inFlight;
}

/**
 * The signed-in user's answer about the ops another account left unsent: discard them
 * and let the outbox take this user's changes, or keep them for that account (the caller
 * signs out).
 */
export async function settleForeignOutbox(discard: boolean): Promise<void> {
  useForeignOutbox.setState({ count: null });
  if (!discard || !userId) return;
  const myGeneration = generation;
  await discardOutbox(userId);
  if (myGeneration !== generation) return;
  outboxAvailable = true;
  announce(OUTBOX_CHANGED);
}

// The question about another account's ops is moot once the outbox is empty or this user's.
async function settleForeignOutboxElsewhere(myGeneration: number): Promise<void> {
  if (useForeignOutbox.getState().count === null) return;
  const owner = await readOutboxUser();
  const settled = owner === userId || (await localDb.outbox.count()) === 0;
  if (!settled || myGeneration !== generation) return;
  useForeignOutbox.setState({ count: null });
  outboxAvailable = true;
}

export function hydrate(forUserId: string): Promise<void> {
  return openStorage(forUserId, generation, false);
}

// After a failed open, on the next round or change. The copy pulled into memory meanwhile
// is newer than the stored one: it stays, and the next page writes it whole.
function reopenStorage(): Promise<void> {
  if (storage !== "failed" || !userId) return storageOpening;
  storage = "opening";
  // Dexie keeps a failed open as its final state and rejects everything after it.
  if (localDb.hasFailed()) localDb.close({ disableAutoOpen: false });
  storageOpening = openStorage(userId, generation, true);
  return storageOpening;
}

const TIMED_OUT = Symbol("timed out");

async function openStorage(forUserId: string, myGeneration: number, keepMemory: boolean): Promise<void> {
  const read = readPersisted();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), STORAGE_OPEN_TIMEOUT_MS);
  });
  const persisted = await Promise.race([read, timedOut]);
  clearTimeout(timer);
  if (myGeneration !== generation) return;
  if (persisted === TIMED_OUT) {
    storage = "failed";
    // A late open is taken up like any retry.
    void read.then((late) => (late && myGeneration === generation ? reopenStorage() : undefined));
    return;
  }
  if (!persisted) {
    storage = "failed";
    return;
  }

  let available = true;
  const storedUser = persisted.meta.get("userId");
  const outboxUser = persisted.meta.get(OUTBOX_USER_KEY);
  if (outboxUser !== forUserId && (outboxUser !== undefined || persisted.outbox.length > 0)) {
    const unsent = unsentAfter(persisted.outbox, ackedThroughIn(persisted.meta)).length;
    // An op with no recorded owner cannot be sent for anyone.
    if (outboxUser !== undefined && unsent > 0) {
      available = false;
      useForeignOutbox.setState({ count: unsent });
    } else {
      await discardOutbox();
    }
    persisted.outbox = [];
  }
  if (storedUser !== forUserId && storedUser !== undefined) await clearLocalDb();
  if (myGeneration !== generation) return;
  // Only now: an op written while the outbox was being discarded would be discarded with it.
  outboxAvailable = available;
  storage = "open";
  keepMemory &&= useTaskStore.getState().meta.cursor > 0;
  if (keepMemory) {
    runId = storedUser === forUserId ? ((persisted.meta.get(RUN_KEY) as string | undefined) ?? null) : null;
    needsFullWrite = true;
  }
  if (storedUser !== forUserId || keepMemory) {
    useTaskStore.getState().upsert([], [], { userId: forUserId });
    showOutbox(persisted.outbox, ackedThroughIn(persisted.meta));
    return;
  }
  adopt(persisted);
}

// Sign-out: this tab forgets the copy, and every other tab stops syncing it. The outbox
// stays for its user's next sign-in, ops still being written included.
// Sign-out must not hang on a stuck IndexedDB.
function outboxWrittenOrTimedOut(): Promise<void> {
  return Promise.race([outboxWritten(), new Promise<void>((resolve) => setTimeout(resolve, STORAGE_OPEN_TIMEOUT_MS))]);
}

export async function wipeLocalCopy(): Promise<void> {
  const written = outboxWrittenOrTimedOut();
  stopSyncEngine();
  useTaskStore.getState().clear();
  announce(SIGNED_OUT);
  await written;
  await clearLocalDb();
}

function listenForTriggers(): () => void {
  const onVisible = () => {
    if (document.visibilityState === "visible") void pull();
    // A hidden tab may be closed before its debounced push.
    else if (!pushPaused && unsentCount(useTaskStore.getState()) > 0) handOutboxToWorker();
  };
  const onOnline = () => void pull();

  let liveTimer: number | undefined;
  const onLiveChange = () => {
    window.clearTimeout(liveTimer);
    liveTimer = window.setTimeout(() => void pull(), LIVE_CHANGE_DEBOUNCE_MS);
  };
  const unsubscribe = subscribeToChanges({ onChange: onLiveChange, onReconnect: onLiveChange });

  // The session cookie is shared: a sign-out in another tab signs this one out too. The
  // reload lands on sign-in, or on whoever signed in there since.
  const tabs = syncChannel();
  const myGeneration = generation;
  tabs?.addEventListener("message", (event: MessageEvent<ChannelMessage>) => {
    const message = event.data;
    if (typeof message === "object" && message !== null) {
      if (message.from === TAB_ID) return;
      if (message.type === "outbox-changed") void refreshOutbox(myGeneration);
      if (message.type === "rejected") report(message.rejections);
    } else if (message === SIGNED_OUT) {
      const written = outboxWrittenOrTimedOut();
      stopSyncEngine();
      useTaskStore.getState().clear();
      void written.then(reloadApp);
    }
  });

  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("online", onOnline);
  return () => {
    unsubscribe();
    tabs?.close();
    window.clearTimeout(liveTimer);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("online", onOnline);
  };
}

export function stopSyncEngine(): void {
  generation += 1;
  userId = null;
  inFlight = null;
  pullAgain = false;
  engineAbort?.abort();
  engineAbort = null;
  runId = null;
  needsFullWrite = false;
  outboxAvailable = false;
  storage = "opening";
  pushPaused = false;
  clearTimeout(pushTimer);
  clearTimeout(retryTimer);
  retryDelay = RETRY_DELAYS_MS.first;
  enqueuedHere.clear();
  unwritten.clear();
  useForeignOutbox.setState({ count: null });
  stopTriggers?.();
  stopTriggers = null;
}

// Follows the signed-in user across Inertia visits: starts on sign-in, restarts on a
// different user, stops on sign-out. Idempotent for the same user.
export function syncForUser(nextUserId: string | null): Promise<void> {
  if (nextUserId === userId) return Promise.resolve();
  stopSyncEngine();
  if (!nextUserId) return Promise.resolve();

  if (useTaskStore.getState().meta.userId !== nextUserId) {
    useTaskStore.getState().clear();
    useTaskStore.getState().upsert([], [], { userId: nextUserId });
  }
  userId = nextUserId;
  requestPersistentStorage();
  engineAbort = new AbortController();
  const myGeneration = generation;
  stopTriggers = listenForTriggers();
  // Joined by pull() like any in-flight pull: a trigger during hydration must not pull
  // from the empty store's cursor 0, which would fetch a full snapshot.
  storageOpening = hydrate(nextUserId);
  inFlight = storageOpening.then(() => runPulls(myGeneration));
  return inFlight;
}
